import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

type Presentacion = 'lateral' | 'centro';
type AnchoCentro = 'md' | 'wide';

export interface CapaModalProps {
	/** Solo se usa con `presentacion='centro'`: `'md'` (340 px, el ancho de `PopUpAviso` por
	 * defecto) o `'wide'` (420 px, `PopUpAviso` `wide`). */
	ancho?: AnchoCentro;
	/** Contenido del diálogo. Incluye su propio título (el elemento cuyo `id` coincide con
	 * `tituloId`) y, si hace falta un botón de cerrar visible, lo pinta el consumidor invocando
	 * `onCerrar` — `CapaModal` no impone ningún botón de cierre propio. */
	children: ReactNode;
	/** Clase adicional para el propio diálogo (compone, no sustituye — mismo criterio que
	 * `Card`/`Alert`). */
	className?: string;
	/**
	 * Cierra la capa. Sin esta prop, la capa queda estrictamente controlada por quien la monta:
	 * ni `Escape` ni un clic en el velo hacen nada — el `onKeyDown`/`onClick` correspondiente
	 * simplemente no se registra.
	 */
	onCerrar?: () => void;
	/** `false` (o ausente) no monta nada: ni velo, ni portal, ni listeners. */
	open: boolean;
	/** `'centro'` (por defecto) es una tarjeta centrada; `'lateral'` es un panel que entra desde
	 * la izquierda, `min(340px, 86vw)` de ancho, pensado para "el mapa de la app" de la cabecera. */
	presentacion?: Presentacion;
	/** Elemento cuyo `id` describe el diálogo (`aria-labelledby`). El título en sí lo pinta el
	 * consumidor dentro de `children`, con ese mismo `id` — `CapaModal` no traduce ni pinta
	 * ningún texto por dentro, igual que `Alert`/`EstadoError`/`BotonCopiar`. */
	tituloId: string;
}

/** Selector de "es alcanzable con Tab" para el atrapado de foco y el foco inicial — mismo criterio
 * mínimo que usan los patrones de menú accesible ya existentes en el repositorio (`menuUsuario.tsx`),
 * ampliado a los controles de formulario que un diálogo puede contener. */
const SELECTOR_FOCOSABLE =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Pila module-level de capas abiertas (una entrada por instancia montada con `open=true`, en
 * orden de apertura). Existe para que dos `CapaModal` anidadas — p. ej. el panel lateral de la
 * cabecera abriendo un `PopUpAviso` de confirmación por encima — no se pisen: `Escape` y el
 * atrapado de `Tab` de una capa solo actúan si esa capa es la de más arriba de la pila. Hace
 * falta a nivel de módulo (no de contexto de React) porque `createPortal` mueve el DOM fuera del
 * árbol del consumidor, pero los eventos SINTÉTICOS de React siguen burbujeando por el árbol de
 * componentes, no por el árbol del DOM — sin esta pila, el `onKeyDown` de la capa de abajo
 * recibiría igualmente el `Tab`/`Escape` de la capa de arriba.
 */
const pilaCapas: string[] = [];

let candadosDeScroll = 0;
let overflowOriginalDelBody = '';

/** Bloquea el scroll del `body` la primera vez que se abre una capa; las siguientes solo suman un
 * candado. Un contador en vez de un booleano porque varias capas pueden estar abiertas a la vez
 * (la pila de arriba) y el scroll no debe restituirse hasta que se cierre la última. */
const bloquearScrollDelBody = (): void => {
	if (candadosDeScroll === 0) {
		overflowOriginalDelBody = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
	}
	candadosDeScroll += 1;
};

const liberarScrollDelBody = (): void => {
	candadosDeScroll = Math.max(0, candadosDeScroll - 1);
	if (candadosDeScroll === 0) {
		document.body.style.overflow = overflowOriginalDelBody;
	}
};

const ANCHURAS_CENTRO: Record<AnchoCentro, string> = {
	md: 'w-[340px]',
	wide: 'w-[420px]',
};

/**
 * La mecánica modal del sistema (no hay ninguna hoy en `components/ui/`, ver
 * `specs/diseno-2026-09-14/especificacion.md`): velo + diálogo portados a `document.body`
 * (obligatorio — la cabecera es `sticky z-30`, así que un diálogo que colgara del árbol normal
 * quedaría por debajo o recortado por su `overflow`), con foco atrapado y restituido, cierre por
 * `Escape`/velo, bloqueo de scroll y pila de solapamientos. Dos consumidores de oleadas
 * posteriores la necesitan idéntica: el panel lateral de la cabecera (`presentacion='lateral'`,
 * "el mapa de la app") y `PopUpAviso` (`presentacion='centro'`).
 *
 * No traduce nada por dentro: título, cuerpo y cualquier etiqueta de botón entran por `children`
 * desde quien consume el componente, con su propio namespace i18n — mismo criterio que
 * `Alert`/`EstadoError`/`BotonCopiar`.
 *
 * `prefers-reduced-motion` apaga la animación de entrada (`motion-safe:` + la variante
 * `starting:` de Tailwind 4/`@starting-style`, sin ningún estado de React para el paso "recién
 * montado" → "en reposo"): quien lo pide apagado ve la capa ya en su posición final desde el
 * primer fotograma, sin transición que desactivar a mano.
 */
export const CapaModal = ({
	ancho = 'md',
	children,
	className = '',
	onCerrar,
	open,
	presentacion = 'centro',
	tituloId,
}: CapaModalProps): React.JSX.Element | null => {
	const id = useId();
	const contenedorRef = useRef<HTMLDivElement>(null);
	const elementoQueAbrioRef = useRef<HTMLElement | null>(null);

	// Apertura: registra la capa en la pila, bloquea el scroll, recuerda quién tenía el foco y lo
	// mueve al primer control focosable de dentro (o al propio diálogo si no hay ninguno). Cierre
	// —por `open` pasando a `false` o por desmontaje mientras seguía abierta, el mismo `return` de
	// limpieza cubre los dos casos— saca la capa de la pila, libera el candado de scroll y
	// devuelve el foco a quien la abrió.
	useEffect(() => {
		if (!open) {
			return;
		}

		elementoQueAbrioRef.current = document.activeElement as HTMLElement | null;
		pilaCapas.push(id);
		bloquearScrollDelBody();

		const primerFocosable = contenedorRef.current?.querySelector<HTMLElement>(SELECTOR_FOCOSABLE);
		(primerFocosable ?? contenedorRef.current)?.focus();

		return () => {
			const indice = pilaCapas.indexOf(id);
			if (indice !== -1) {
				pilaCapas.splice(indice, 1);
			}
			liberarScrollDelBody();
			elementoQueAbrioRef.current?.focus();
		};
	}, [open, id]);

	// `Escape` cierra, pero solo la capa de más arriba de la pila (ver JSDoc de `pilaCapas`) y
	// solo si se pasa `onCerrar`.
	useEffect(() => {
		if (!open || !onCerrar) {
			return;
		}

		const alPulsarTecla = (evento: KeyboardEvent): void => {
			if (evento.key !== 'Escape' || pilaCapas[pilaCapas.length - 1] !== id) {
				return;
			}
			onCerrar();
		};

		document.addEventListener('keydown', alPulsarTecla);
		return () => document.removeEventListener('keydown', alPulsarTecla);
	}, [open, onCerrar, id]);

	if (!open) {
		return null;
	}

	// Foco atrapado (`Tab`/`Shift+Tab` circulan dentro del diálogo): igual que `Escape`, solo actúa
	// si esta capa es la de más arriba de la pila, para que una capa anidada no le robe el `Tab` a
	// la de más abajo (ver JSDoc de `pilaCapas`).
	const alPulsarTeclaEnDialogo = (evento: React.KeyboardEvent<HTMLDivElement>): void => {
		if (evento.key !== 'Tab' || pilaCapas[pilaCapas.length - 1] !== id || !contenedorRef.current) {
			return;
		}

		const focosables = Array.from(contenedorRef.current.querySelectorAll<HTMLElement>(SELECTOR_FOCOSABLE));
		if (focosables.length === 0) {
			evento.preventDefault();
			return;
		}

		const primero = focosables[0];
		const ultimo = focosables[focosables.length - 1];
		if (!primero || !ultimo) {
			return;
		}
		if (evento.shiftKey && document.activeElement === primero) {
			evento.preventDefault();
			ultimo.focus();
		} else if (!evento.shiftKey && document.activeElement === ultimo) {
			evento.preventDefault();
			primero.focus();
		}
	};

	const clasesDialogo =
		presentacion === 'lateral'
			? 'h-full w-[min(340px,86vw)] overflow-y-auto rounded-r-xl bg-paper shadow-modal motion-safe:translate-x-0 motion-safe:transition-transform motion-safe:duration motion-safe:ease-bounce motion-safe:starting:-translate-x-full'
			: `max-h-[86vh] overflow-y-auto rounded-xl bg-paper shadow-modal motion-safe:scale-100 motion-safe:opacity-100 motion-safe:transition-all motion-safe:duration motion-safe:ease-bounce motion-safe:starting:scale-95 motion-safe:starting:opacity-0 ${ANCHURAS_CENTRO[ancho]}`;

	return createPortal(
		<div className='fixed inset-0 z-50'>
			{/* Velo decorativo: el cierre por teclado ya lo cubre `Escape` a nivel de documento, así
			que este `div` no necesita entrar en el orden de tabulación ni anunciarse a un lector de
			pantalla — es un atajo de ratón/táctil sobre una capa que ya es cerrable sin él. */}
			<div aria-hidden='true' className='absolute inset-0 bg-veil' onClick={onCerrar} />
			<div className={presentacion === 'lateral' ? 'relative h-full' : 'flex h-full items-center justify-center p-4'}>
				<div
					aria-labelledby={tituloId}
					aria-modal='true'
					className={`relative ${clasesDialogo} ${className}`}
					onKeyDown={alPulsarTeclaEnDialogo}
					ref={contenedorRef}
					role='dialog'
					tabIndex={-1}
				>
					{children}
				</div>
			</div>
		</div>,
		document.body,
	);
};
